import { first } from 'utils';
import { TRACKING_LABEL } from '../constants';
import type { OrderType, SubOrderStatusType } from './types';

/**
 * Determines the appropriate tracking label for an order based on its sub-orders' delivery status.
 *
 * This function selects tracking labels to reflect the current state of an order,
 * especially when dealing with partial deliveries (multiple sub-orders with different statuses).
 *
 * Logic flow:
 * 1. **Single sub-order**: Returns the tracking label for that sub-order's state code
 * 2. **Multiple sub-orders with partial delivery**:
 *    - If any sub-order is DELIVERED → 'Teillieferung Zugestellt' (Partially Delivered)
 *    - If any sub-order is SHIPPED → 'Teillieferung Versand' (Partially Shipped)
 * 3. **Fallback**: Returns the tracking label for the main order status
 *
 * The tracking labels are localized German strings from TRACKING_LABEL constants:
 * - 'Bestellung' (Order)
 * - 'Druck' (Print/Processing)
 * - 'Versand' (Shipped)
 * - 'Zugestellt' (Delivered)
 * - 'Teillieferung Versand' (Partially Shipped)
 * - 'Teillieferung Zugestellt' (Partially Delivered)
 *
 * @param subOrders - Array of sub-orders with their status information
 * @param order - The main order object containing overall status
 * @returns Tracking label string representing the current delivery state
 *
 * @example
 * ```typescript
 * // Single sub-order that's shipped
 * const label = getTrackingLabelForSubOrders(
 *   [{ stateCode: 'SHIPPED', ... }],
 *   { status: 'PROCESSING', ... }
 * );
 * // Returns: 'Versand'
 *
 * // Multiple sub-orders with partial delivery
 * const label = getTrackingLabelForSubOrders(
 *   [
 *     { stateCode: 'DELIVERED', ... },
 *     { stateCode: 'PROCESSING', ... }
 *   ],
 *   { status: 'PROCESSING', ... }
 * );
 * // Returns: 'Teillieferung Zugestellt' (Partially Delivered)
 *
 * // Multiple sub-orders with partial shipment
 * const label = getTrackingLabelForSubOrders(
 *   [
 *     { stateCode: 'SHIPPED', ... },
 *     { stateCode: 'PROCESSING', ... }
 *   ],
 *   { status: 'PROCESSING', ... }
 * );
 * // Returns: 'Teillieferung Versand' (Partially Shipped)
 * ```
 */
export function getTrackingLabelForSubOrders(subOrders: SubOrderStatusType[], order: OrderType) {
  if (subOrders.length === 1) {
    const firstOrder = first(subOrders, subOrders[0]);

    return firstOrder ? TRACKING_LABEL[firstOrder.stateCode] : TRACKING_LABEL[order.status];
  } else if (subOrders.length > 1) {
    const hasDeliveredOrder = !!subOrders.find((subOrder: SubOrderStatusType) => subOrder.stateCode === 'DELIVERED');

    if (hasDeliveredOrder) {
      return TRACKING_LABEL['PARTIALLY_DELIVERED'];
    }

    const hasShippedOrder = !!subOrders.find((subOrder: SubOrderStatusType) => subOrder.stateCode === 'SHIPPED');

    if (hasShippedOrder) {
      return TRACKING_LABEL['PARTIALLY_SHIPPED'];
    }
  }

  return TRACKING_LABEL[order.status];
}
